import styled from 'styled-components'
import { CustomScrollbarMixin } from '../../../../css/CustomScrollbarMixin'
import { BreakpointWidthPx } from '../../../../css/BreakpointWidthPx'
import { SvgTick } from '../../../../components/SvgImports'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { getMenuState, setMenuTreeAllView } from '../../../../store/menuSlice'
import { getSessionInitDataState } from '../../../../store/sessionDataSlice'
import { useClient } from '../../../../utils/useClient'
import { BlendColorRgb, BlendColors } from '../../BlendColors'
import { useLayoutEffect, useRef } from 'react'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingMenuClickEvent,
  pushAdobeEvent,
} from '../../../../utils/adobeAnalytics'
import { TextId, useTextsV2 } from '../../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../../css/BreakpointHeightPx'
import { Size2d } from '../../../../types/Size2d'
import { blinkKeyframesColor } from '../../MenuBlinkAnimationColor'
import { ViewAllOrLessOptionsButton } from '../../GuidedViewPage/ViewAllOrLessOptionsButtonMobile'
import {
  TdsDropdown,
  TdsDropdownOption,
  TdsSpinner,
  TdsTooltip,
} from '@scania/tegel-react'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../../utils/getUrlCidParameter'
import { TestElementTypeId } from '../../../../types/TestAttributeId'

const MenuContentRoot = styled.div`
  ${CustomScrollbarMixin}
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  padding-right: 24px;
  overflow-y: scroll;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding: 16px;
    overflow-y: unset;
    flex: unset;
  }
`

const Header = styled.h5`
  margin-bottom: 8px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: bold;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 14px;
  }
`

const SelectedOptionWrapper = styled.div`
  display: flex;
  margin-top: 32px;
  margin-bottom: 16px;

  .boldText {
    font-weight: bold;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  box-sizing: content-box;
  margin: 8px;
  margin-top: 16px;
`

interface CardProps {
  $isSelected: boolean
  $color: string
  $isMetallic: boolean
  $menuLevel: number
  $isHighlighted: boolean
}

const CardWrapper = styled.div`
  width: 68px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 60px;
    margin-bottom: 10px;
  }
`

const CardWrapperLeaf = styled.div`
  width: 68px;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  align-items: center;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 60px;
    margin-bottom: 10px;
  }
`

const ColourCircle = styled.div<CardProps>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  outline: ${(props) => (props.$isSelected ? '2px solid black' : 'none')};
  outline-offset: 2px;
  display: flex;

  background: ${(props) =>
    !props.$isMetallic
      ? props.$color
      : props.$menuLevel === 3
      ? `linear-gradient(to bottom, black, #ffffff)`
      : `linear-gradient(to bottom, ${props.$color} 55%, #ffffff)`};

  animation-duration: 500ms;
  animation-iteration-count: 3;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframesColor : 'unset'};

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 48px;
    height: 48px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const ColourText = styled.div`
  margin-top: 12px;
  font-size: 12px;
  margin-left: 3px;
  margin-right: 3px;
  max-width: 100%;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const TickWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const LoadingWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;
`

// Important to not include background-color here and set it with inline CSS on
// the element attribute instead to avoid styled-components generating "over 200
// classes", causing a visible lag in the GUI.
// This can be solved with the .attrs function but that results in some super
// ugly syntax.

const blendColorToCss = (rgb: BlendColorRgb): string => {
  return `rgb(${rgb.R}, ${rgb.G}, ${rgb.B})`
}

const getColorVariantRgb = (id: string): BlendColorRgb | undefined => {
  return BlendColors[id]
}

interface ColourProps {
  handleItemSelection: (id: string, text: string) => void
  parentSize: Size2d
  highlightedId: string | null
  selectedMenuLevel2: string | null | undefined
  selectedMenuLevel3: string | null | undefined
}

export function MenuContentColourAllView({
  handleItemSelection,
  parentSize,
  highlightedId,
  selectedMenuLevel2,
  selectedMenuLevel3,
}: ColourProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const client = useClient()
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  const menuTree = useAppSelector(getMenuState).menuResultAllView?.menuTree
  const selectedCabColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedCabColorNode
  const selectedChassiColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedChassiColorNode
  const menuLevel2 = menuTree?.subMenus
    ? menuTree?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel3 = menuLevel2
    ? menuLevel2?.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel4 = menuLevel3
    ? menuLevel3.find((menu) => menu.isOpen === true)?.leafItems
    : null
  const isCabColour = menuLevel2
    ? menuLevel2.find((menu) => menu.isOpen === true)?.id ===
      'SUBMENU_GV_COLOR_CAB'
    : false
  const scrollToHighlightedItemRef = useRef<HTMLDivElement | null>(null)
  const scrollToSelectedItemRef = useRef<HTMLDivElement | null>(null)
  const scrollToColorLeafItemRef = useRef<HTMLDivElement | null>(null)
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')

  const handleSubMenuClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  // SC1M-3105: "Cab colour" and "Chassi colour" should not be displayed, just the colour
  const getTextWithoutCabColour = (
    id: string,
    text: string | null | undefined,
  ) => {
    var updatedText = text
    if (id === 'SUBMENU_02575' || id === 'SUBMENU_06676') {
      updatedText = t2(TextId.LABEL_COLOR_02575)
    }
    if (id === 'SUBMENU_06482' || id === 'SUBMENU_06773') {
      updatedText = t2(TextId.LABEL_COLOR_06482)
    }
    if (id === 'SUBMENU_06483' || id === 'SUBMENU_06774') {
      updatedText = t2(TextId.LABEL_COLOR_06483)
    }
    if (id === 'SUBMENU_06484' || id === 'SUBMENU_06775') {
      updatedText = t2(TextId.LABEL_COLOR_06484)
    }
    if (id === 'SUBMENU_06485' || id === 'SUBMENU_06776') {
      updatedText = t2(TextId.LABEL_COLOR_06485)
    }
    if (id === 'SUBMENU_06486' || id === 'SUBMENU_06777') {
      updatedText = t2(TextId.LABEL_COLOR_06486)
    }
    if (id === 'SUBMENU_06487' || id === 'SUBMENU_06778') {
      updatedText = t2(TextId.LABEL_COLOR_06487)
    }
    if (id === 'SUBMENU_06488' || id === 'SUBMENU_06779') {
      updatedText = t2(TextId.LABEL_COLOR_06488)
    }
    if (id === 'SUBMENU_06489' || id === 'SUBMENU_06780') {
      updatedText = t2(TextId.LABEL_COLOR_06489)
    }
    if (id === 'SUBMENU_06490' || id === 'SUBMENU_06781') {
      updatedText = t2(TextId.LABEL_COLOR_06490)
    }
    if (id === 'SUBMENU_06491' || id === 'SUBMENU_06782') {
      updatedText = t2(TextId.LABEL_COLOR_06491)
    }
    if (id === 'SUBMENU_06492' || id === 'SUBMENU_06783') {
      updatedText = t2(TextId.LABEL_COLOR_06492)
    }
    if (!updatedText) {
      updatedText = id
    }
    return updatedText
  }

  const handleSubMenuDropdownSelection = (
    evt: CustomEvent<{ name: string; value: string }>,
  ) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const selectedSubMenuId = evt.detail.value
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: selectedSubMenuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: selectedSubMenuId,
            optionClickedText: '',
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  // Scroll down to highlighted item
  useLayoutEffect(() => {
    const scrollDelayMs = 100
    setTimeout(() => {
      if (scrollToHighlightedItemRef.current) {
        scrollToHighlightedItemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }, scrollDelayMs)
  }, [highlightedId])

  // Scroll down to selected item
  const handleSelectedClick = () => {
    if (scrollToSelectedItemRef.current) {
      scrollToSelectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  // Scroll down to color choices
  const handleColourGroupClick = (id: string, text: string) => {
    handleSubMenuClick(id, text)
    if (scrollToColorLeafItemRef.current) {
      scrollToColorLeafItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  return (
    <MenuContentRoot>
      <ViewAllOrLessOptionsButton
        parentSize={parentSize}
        data-test-element-type={TestElementTypeId.AllViewViewLessButton}
      />
      <Header>{t2(TextId.HEADER_GV_PRODUCT_TYPE)}</Header>
      {menuLevel2 && menuLevel2[0] && (
        <TdsDropdown
          size="sm"
          open-direction="auto"
          name="role"
          defaultValue={selectedMenuLevel2 || undefined}
          onTdsChange={handleSubMenuDropdownSelection}
          selected-option={selectedMenuLevel2}
          key={selectedMenuLevel2}
          data-test-element-type={TestElementTypeId.AllViewDropDownLevel2}
        >
          {menuLevel2.map((menuItem, i) => (
            <TdsDropdownOption
              key={'dropdownLevel2' + menuItem.id + i}
              value={menuItem.id}
              data-test-etel-variant-id={menuItem.id}
            >
              {menuItem.shortText}
            </TdsDropdownOption>
          ))}
        </TdsDropdown>
      )}

      <Header>{t2(TextId.HEADER_GV_PRODUCT_OPTIONS)}</Header>
      {menuLevel2 &&
      menuLevel3 &&
      (menuLevel2[0].isOpen || menuLevel2[1].isOpen) ? (
        <CardsWrapper>
          {menuLevel3.map((menuItem, i) => {
            return (
              <CardWrapper
                key={menuItem.id}
                onClick={() =>
                  handleColourGroupClick(menuItem.id, menuItem.shortText || '')
                }
                id={'colorCard' + menuItem.id}
                data-test-element-type={
                  TestElementTypeId.AllViewColourCardLevel3
                }
              >
                <ColourCircle
                  $isSelected={menuItem.isOpen}
                  $color={blendColorToCss(
                    getColorVariantRgb(menuItem.id) || {
                      R: 255,
                      G: 255,
                      B: 255,
                    },
                  )}
                  $isMetallic={menuItem.id.includes('02575')}
                  $menuLevel={3}
                  $isHighlighted={menuItem.id === highlightedId}
                />
                <ColourText>
                  {getTextWithoutCabColour(menuItem.id, menuItem.shortText)}
                </ColourText>
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="bottom"
                    selector={'#colorCard' + menuItem.id}
                    text={menuItem.id}
                  />
                )}
              </CardWrapper>
            )
          })}
        </CardsWrapper>
      ) : (
        <TdsDropdown
          size="sm"
          open-direction="auto"
          name="role"
          defaultValue={selectedMenuLevel3 || undefined}
          onTdsChange={handleSubMenuDropdownSelection}
          selected-option={selectedMenuLevel3}
          key={selectedMenuLevel3}
          data-test-element-type={TestElementTypeId.AllViewDropDownLevel3}
        >
          {menuLevel3?.map((menuItem, i) => (
            <TdsDropdownOption
              key={'dropdownLevel3' + menuItem.id + i}
              value={menuItem.id}
              data-test-etel-variant-id={menuItem.id}
            >
              {menuItem.shortText}
            </TdsDropdownOption>
          ))}
        </TdsDropdown>
      )}
      <SelectedOptionWrapper
        ref={scrollToColorLeafItemRef}
        onClick={handleSelectedClick}
      >
        <div>{t2(TextId.GV_SELECTED)}&nbsp; </div>
        <div className="boldText">
          {isCabColour
            ? selectedCabColourNode?.shortText
            : selectedChassiColourNode?.shortText}
        </div>
      </SelectedOptionWrapper>

      <CardsWrapper>
        {menuLevel4 &&
          menuLevel4.map((leafItem, i) => {
            return (
              <CardWrapperLeaf
                key={leafItem.id}
                onClick={
                  leafItem.isSelected
                    ? () => {}
                    : () =>
                        handleItemSelection(
                          leafItem.id,
                          leafItem.shortText || '',
                        )
                }
                ref={
                  leafItem.id === highlightedId
                    ? scrollToHighlightedItemRef
                    : leafItem.isSelected
                    ? scrollToSelectedItemRef
                    : null
                }
                id={'colorCardLeaf' + i}
                data-test-element-type={
                  TestElementTypeId.AllViewColourCardLevel4
                }
              >
                <ColourCircle
                  $isSelected={leafItem.isSelected}
                  $color={blendColorToCss(
                    getColorVariantRgb(leafItem.id) || { R: 255, G: 0, B: 255 },
                  )}
                  $isMetallic={leafItem.id.includes('02575')}
                  $menuLevel={4}
                  $isHighlighted={leafItem.id === highlightedId}
                >
                  {leafItem.isSelected && (
                    <TickWrapper>
                      <SvgTick height="24px" />
                    </TickWrapper>
                  )}
                  {currentlyLoadingId === leafItem.id && (
                    <LoadingWrapper>
                      <TdsSpinner size="sm" />
                    </LoadingWrapper>
                  )}
                </ColourCircle>
                <ColourText>
                  {leafItem.shortText ? leafItem.shortText : leafItem.id}
                </ColourText>
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="top"
                    selector={'#colorCardLeaf' + i}
                    text={leafItem.id}
                  />
                )}
              </CardWrapperLeaf>
            )
          })}
      </CardsWrapper>
    </MenuContentRoot>
  )
}
